import { DateTime } from 'luxon';

import type { PayerOrganizationRead } from 'src/common/external/bambi-api/bambiApi';

import type { AddTripFormValues, Trip } from '../FormValues';

export interface ValidationError {
  attr: 'Timing' | 'Passenger';
  detail: string;
}

export const validateFormInputs = (
  formValues: AddTripFormValues,
  selectedPayer?: PayerOrganizationRead | null,
  isTripRequiredFieldsEnabled?: boolean
): ValidationError[] => {
  const errors: ValidationError[] = [];

  const timingError = validateTripTiming(formValues.trips);
  if (timingError) errors.push(timingError);

  if (isTripRequiredFieldsEnabled) {
    const passengerErrors = validatePassengerInformation(
      formValues,
      selectedPayer
    );
    if (passengerErrors) errors.push(...passengerErrors);
  }

  return errors;
};

const validateTripTiming = (trips: Trip[]): ValidationError | null => {
  if (trips.length > 1) {
    for (let i = 1; i < trips.length; i++) {
      const previousTripDate = trips[i - 1].timing.date;
      const currentTripDate = trips[i].timing.date;
      const previousTripPickupTime = trips[i - 1].timing.pickup_time;
      const currentTripPickupTime = trips[i].timing.pickup_time;

      const previousTripDateAndTime = DateTime.fromISO(
        `${previousTripDate}T${previousTripPickupTime}`
      );
      const currentTripDateAndTime = DateTime.fromISO(
        `${currentTripDate}T${currentTripPickupTime}`
      );

      if (currentTripDateAndTime < previousTripDateAndTime) {
        return {
          attr: 'Timing',
          detail: `Trip ${i + 1} is scheduled before trip ${i}`,
        };
      }
    }
  }
  return null;
};

const validatePassengerInformation = (
  formValues: AddTripFormValues,
  selectedPayer?: PayerOrganizationRead | null
): ValidationError[] | null => {
  if (!selectedPayer?.trip_required_fields) return null;

  const { passenger_weight, passenger_phone_number } =
    selectedPayer.trip_required_fields;
  const errors: ValidationError[] = [];

  if (passenger_weight && !formValues.passenger.weight) {
    errors.push(
      createError(
        'Passenger',
        'Weight is required. Please click "Edit Selected Passenger" to add a weight.'
      )
    );
  }

  if (passenger_phone_number && !formValues.passenger.phone_number) {
    errors.push(
      createError(
        'Passenger',
        'Phone number is required. Please click "Edit Selected Passenger" to add a phone number.'
      )
    );
  }

  return errors.length > 0 ? errors : null;
};

const createError = (
  attr: ValidationError['attr'],
  detail: string
): ValidationError => ({
  attr,
  detail,
});
